import { type PayloadAction, createSlice, isRejected } from "@reduxjs/toolkit"
import { REHYDRATE } from "redux-persist/es/constants"

import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import { type RehydrateAction } from "../../../store.ts"
import { guessTimezone } from "../../Calendar/CalendarPage/components/CalendarAddForms/time.ts"
import { type PermissionFeatures } from "../../SettingsPermissions/types/permissions.types.ts"
import { type RefreshedSession } from "../../../services/token-refresh.ts"
import { type UserInfo } from "../types/auth.types.ts"
import { mergePermissions } from "./merge-permissions.ts"
import {
  checkPassword,
  confirmEmail,
  confirmPassword,
  createAccount,
  createUserPassword,
  logout,
  refreshToken,
  resendVerificationLink,
  signIn,
  socialAuth,
  verifyToken,
  whatsappGoogleLogin,
  whatsappLogin
} from "./auth.thunks.ts"
import { type AuthState } from "./auth.type.ts"
import { isSentToMobileApp } from "./send-to-mobile-app"

const initialState: AuthState = {
  userInfo: null as any,
  codeValid: false,
  isPasswordCreated: false,
  accessToken: null,
  refreshToken: null,
  isValidRefreshToken: false,
  error: null,
  fetching: false,
  isLogoutPending: false,

  updateFetching: false,
  updateErrors: false,

  isAdmin: false,
  role: null,
  permissions: {
    pages: [],
    labels: [],
    // todo fix it later
    codes: {} as PermissionFeatures
  },

  emailSent: false,
  isWhatsAppConnected: false,

  rememberMe: null,
  saveResult: null
}

// Slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authSetProp: generateSetPropReducer<AuthState>(),
    authErrorReset: (state) => {
      state.error = null
      state.fetching = false
    },
    clearState: (state) => {
      return {
        ...initialState,
        isPasswordCreated: state.isPasswordCreated
      }
    },
    // No `?? state.refreshToken` guard, unlike refreshToken.fulfilled below:
    // the predicate that produces this payload requires both halves.
    pairAdopted: (state, action: PayloadAction<RefreshedSession>) => {
      state.accessToken = action.payload.access
      state.refreshToken = action.payload.refresh
    },
    updateProfile: (state, action) => {
      const data = action.payload

      const timezone = data.user?.timezone || data?.timezone || guessTimezone()

      if (data?.user) {
        state.userInfo = {
          ...state.userInfo,
          ...data.user,
          timezone,
          role: state?.userInfo?.role
        }
      } else {
        state.userInfo = {
          ...state.userInfo,
          ...data,
          timezone,
          role: data?.role || state?.userInfo?.role
        }
      }

      if (data?.permissions) {
        state.permissions = data?.permissions
      }
    },
    setIsPasswordCreated: (state, action) => {
      state.isPasswordCreated = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
      const authState = action?.payload?.auth
      if (!authState) return state

      // A tab that closed or crashed mid-logout can persist `isLogoutPending:
      // true` — logout's own `condition` guard then blocks every future
      // logout attempt in this browser forever, leaving stale tokens in
      // place and every request stuck 401-ing with no way out but clearing
      // storage by hand. Same reasoning as `fetching`: an in-flight request
      // flag cannot have survived the reload that is rehydrating it.
      return { ...authState, fetching: false, isLogoutPending: false }
    })
    builder
      // signIn
      .addCase(signIn.pending, (state) => {
        state.fetching = true
      })
      .addCase(signIn.fulfilled, (state, action) => {
        const { userInfo = {}, accessToken, refreshToken } = action.payload
        const role = userInfo?.role?.id

        state.fetching = false
        state.error = null
        state.userInfo = {
          ...userInfo,
          timezone: userInfo?.timezone || guessTimezone()
        }
        state.accessToken = accessToken
        state.refreshToken = refreshToken
        state.role = role
        state.permissions = userInfo?.permissions
      })
      .addCase(signIn.rejected, (state, action: PayloadAction<any>) => {
        state.fetching = false
        state.error = { ...state.error, ...action.payload }
      })

      // createAccount
      .addCase(createAccount.pending, (state) => {
        state.fetching = true
        state.error = null
      })
      .addCase(createAccount.fulfilled, (state, action) => {
        const { userInfo, accessToken, refreshToken } = action.payload
        const role = userInfo?.role?.id

        state.fetching = false
        state.error = null
        state.userInfo = userInfo
        state.accessToken = accessToken
        state.refreshToken = refreshToken
        state.role = role
        state.permissions = userInfo?.permissions
      })
      .addCase(createAccount.rejected, (state, action: PayloadAction<any>) => {
        state.fetching = false
        state.error = { ...state.error, ...action.payload }
      })

      // resendVerificationLink
      .addCase(resendVerificationLink.pending, (state) => {
        state.fetching = true
        state.error = null
      })
      .addCase(resendVerificationLink.fulfilled, (state) => {
        state.fetching = false
        state.error = null
      })
      .addCase(resendVerificationLink.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload
      })

      // confirmEmail
      .addCase(confirmEmail.pending, (state) => {
        state.fetching = true
        state.error = null
      })
      .addCase(confirmEmail.fulfilled, (state, action) => {
        const { userInfo, accessToken, refreshToken } = action.payload
        const role = userInfo?.role?.id

        state.fetching = false
        state.error = null
        state.userInfo = userInfo
        state.accessToken = accessToken
        state.refreshToken = refreshToken
        state.role = role
        state.permissions = userInfo?.permissions
      })
      .addCase(confirmEmail.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload
      })

      // checkPassword
      .addCase(checkPassword.pending, (state) => {
        state.fetching = true
        state.error = null
      })
      .addCase(checkPassword.fulfilled, (state) => {
        state.fetching = false
        state.error = null
      })
      .addCase(checkPassword.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload
      })

      // confirmPassword
      .addCase(confirmPassword.pending, (state) => {
        state.fetching = true
        state.error = null
      })
      .addCase(confirmPassword.fulfilled, (state, action) => {
        const { accessToken, refreshToken } = action.payload
        state.fetching = false
        state.error = null
        if (accessToken) state.accessToken = accessToken
        if (refreshToken) state.refreshToken = refreshToken
      })
      .addCase(confirmPassword.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload
      })

      // createUserPassword
      .addCase(createUserPassword.pending, (state) => {
        state.fetching = true
        state.error = null
      })
      .addCase(createUserPassword.fulfilled, (state) => {
        state.fetching = false
        state.error = null
        state.isPasswordCreated = false
        state.saveResult = {
          type: "password_created",
          message: "Password updated successfully."
        }
      })
      .addCase(createUserPassword.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload
      })

      // logout
      .addCase(logout.pending, (state) => {
        state.isLogoutPending = true
        state.fetching = true
      })
      .addCase(logout.fulfilled, (state, action) => {
        localStorage.removeItem("completely_finished")
        return {
          ...initialState,
          error: action.meta.arg?.reason ?? null,
          rememberMe: state.rememberMe
        }
      })
      .addCase(logout.rejected, (state) => {
        state.isLogoutPending = false
        state.fetching = false
      })

      // refreshToken
      .addCase(refreshToken.pending, (state) => {
        state.fetching = true
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        const { access, refresh, user } = action.payload
        state.fetching = false
        state.accessToken = access
        state.refreshToken = refresh ?? state.refreshToken
        if (user) {
          state.userInfo = user
          state.role = user.role?.id ?? state.role
          state.permissions =
            mergePermissions(user.permissions, state.permissions) ??
            state.permissions
        }
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload
      })

      // verifyToken
      .addCase(verifyToken.pending, (state) => {
        state.fetching = true
        state.isValidRefreshToken = false
      })
      .addCase(verifyToken.fulfilled, (state) => {
        state.fetching = false
        state.isValidRefreshToken = true
      })
      .addCase(verifyToken.rejected, (state, action) => {
        state.fetching = false
        state.isValidRefreshToken = false
        state.error = action.payload
      })

      // socialAuth
      .addCase(socialAuth.pending, (state) => {
        state.fetching = true
        state.error = null
      })
      .addCase(socialAuth.fulfilled, (state, action) => {
        const data = action?.payload

        const user = data?.user

        state.fetching = false
        state.error = null
        state.userInfo = user
        state.accessToken = data?.access
        state.refreshToken = data?.refresh
        state.role = user?.role?.id
        state.permissions = mergePermissions(
          user?.permissions,
          state.permissions
        ) as AuthState["permissions"]
      })
      .addCase(socialAuth.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload
      })

      // whatsappLogin
      .addCase(whatsappLogin.pending, (state) => {
        state.fetching = true
        state.error = null
      })
      .addCase(whatsappLogin.fulfilled, (state) => {
        state.fetching = false
        state.error = null
        state.isWhatsAppConnected = true
      })
      .addCase(whatsappLogin.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload
      })

      // whatsappGoogleLogin
      .addCase(whatsappGoogleLogin.pending, (state) => {
        state.fetching = true
        state.error = null
      })
      .addCase(whatsappGoogleLogin.fulfilled, (state) => {
        state.fetching = false
        state.error = null
      })
      .addCase(whatsappGoogleLogin.rejected, (state, action) => {
        state.fetching = false
        state.error = action.payload
      })
      .addMatcher(
        (action) => isRejected(action) && isSentToMobileApp(action.payload),
        (state) => {
          state.error = null
        }
      )
  }
})

export const {
  authErrorReset,
  clearState,
  pairAdopted,
  updateProfile,
  setIsPasswordCreated,
  authSetProp
} = authSlice.actions

export default authSlice
